// Top bar widgets: StoreSwitcher, SearchTrigger, NotificationsBell, UserMenu (+ Profile dialog), HelpMenu.
import { useEffect, useState } from 'react'
import * as P from '@radix-ui/react-popover'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell, BellOff, Check, CheckCheck, ChevronsUpDown, CircleHelp, Command as CommandIcon, Keyboard, LayoutGrid, LogOut, Monitor, Moon, Plus,
  Search, Shield, Sun, UserRound,
} from 'lucide-react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command'
import { Avatar } from '@/components/ui/display'
import { Button, IconButton } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/badge'
import { Kbd } from '@/components/ui/kbd'
import { DropdownMenu, MenuItem, MenuLabel, MenuRadioGroup, MenuSeparator } from '@/components/ui/menu'
import { Dialog } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton, errorMessage } from '@/components/ui/feedback'
import { toast } from '@/components/ui/toast'
import { withReauth } from '@/components/ui/reauth'
import { cn } from '@/components/ui/cn'
import { useSession } from '@/lib/session'
import { api, ApiError } from '@/lib/api'
import { setThemePreference, useTheme, type ThemePreference } from '@/lib/theme'
import { relativeTime } from '@/lib/format'
import { useStoreEvents } from '@/lib/hooks'
import { useGuardedNavigate, useSwitchStore } from './shell-context'

// ─── Store switcher ─────────────────────────────────────────────────────────────────────

export function StoreSwitcher({ compact }: { compact?: boolean }) {
  const { me, store, storeId } = useSession()
  const switchStore = useSwitchStore()
  const go = useGuardedNavigate()
  const [open, setOpen] = useState(false)
  const stores = me?.stores ?? []
  const current = stores.find(s => s.id === storeId)
  const name = store?.name ?? current?.name ?? 'Store'
  return (
    <P.Root open={open} onOpenChange={setOpen}>
      <P.Trigger asChild>
        <button type="button" aria-label={`Current store: ${name}. Switch store`}
          className="group flex h-8 min-w-0 items-center gap-2 rounded-control pr-2 pl-1 text-left transition-colors hover:bg-chrome-hover focus-visible:outline-2 focus-visible:outline-focus data-[state=open]:bg-chrome-hover">
          <Avatar name={name} size={24} square />
          <span className={cn('min-w-0 truncate text-base font-semibold text-text', compact ? 'max-w-[40vw]' : 'max-w-[150px]')}>{name}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-text-subtle group-hover:text-text-muted" />
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content align="start" sideOffset={6} collisionPadding={8}
          className="z-[60] w-[300px] max-w-[calc(100vw-16px)] overflow-hidden rounded-card border border-border bg-surface-raised shadow-popover data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out">
          <Command loop>
            {stores.length > 5 && <CommandInput placeholder="Find a store…" className="h-10 text-base" />}
            <CommandList className="max-h-80">
              <CommandEmpty>No stores match.</CommandEmpty>
              <CommandGroup heading="Your stores">
                {stores.map(s => (
                  <CommandItem key={s.id} value={`${s.name} ${s.slug} ${s.host}`} className="h-auto py-2"
                    icon={<Avatar name={s.name} size={24} square />}
                    end={<span className="flex items-center gap-2">{s.status !== 'active' && <StatusBadge status={s.status} size="sm" />}{s.id === storeId && <Check className="text-primary-strong" />}</span>}
                    onSelect={() => { setOpen(false); void switchStore(s.id) }}>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{s.name}</span>
                      <span className="truncate text-xs text-text-subtle">{s.host} · {s.role}</span>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem value="__all-stores" icon={<LayoutGrid />} onSelect={() => { setOpen(false); void go('/stores') }}>All stores</CommandItem>
                <CommandItem value="__create-store" icon={<Plus />} onSelect={() => { setOpen(false); void go('/onboarding?new=1') }}>Create store</CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </P.Content>
      </P.Portal>
    </P.Root>
  )
}

// ─── Search trigger ─────────────────────────────────────────────────────────────────────

export function SearchTrigger({ onOpen, iconOnly, placeholder = 'Search or jump to…' }: { onOpen: () => void; iconOnly?: boolean; placeholder?: string }) {
  if (iconOnly) return <IconButton icon={<Search />} label="Search" onClick={onOpen} />
  return (
    <button type="button" onClick={onOpen} aria-label="Search and commands" aria-keyshortcuts="Control+K Meta+K"
      className="group flex h-8 w-full max-w-[480px] items-center gap-2 rounded-control border border-border bg-surface/80 px-3 text-base text-text-subtle shadow-control transition-colors hover:border-border-strong hover:bg-surface hover:text-text-muted focus-visible:outline-2 focus-visible:outline-focus dark:bg-white/[0.03]">
      <Search className="size-4 shrink-0" />
      <span className="flex-1 truncate text-left">{placeholder}</span>
      <Kbd combo="mod+k" />
    </button>
  )
}

// ─── Notifications ──────────────────────────────────────────────────────────────────────

import { parseFeed, type NotificationFeed } from './notification-feed'

export function NotificationsBell() {
  const { storeId } = useSession()
  const qc = useQueryClient()
  const go = useGuardedNavigate()
  const [open, setOpen] = useState(false)
  const key = ['notifications', storeId]
  const q = useQuery<NotificationFeed>({
    queryKey: key, enabled: !!storeId, retry: false, staleTime: 30_000,
    refetchInterval: (query) => (query.state.data?.unavailable ? false : 90_000),
    refetchOnWindowFocus: true,
    queryFn: async ({ signal }): Promise<NotificationFeed> => {
      try { return parseFeed(await api.get<unknown>('/api/admin/notifications', { signal })) }
      catch (e) { if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return { items: [], unread: 0, unavailable: true }; throw e }
    },
  })
  useStoreEvents(e => { if (e.type.startsWith('notification.') || e.type === 'order.created') void qc.invalidateQueries({ queryKey: ['notifications'] }) }, { invalidate: false })
  const markRead = useMutation({
    mutationFn: (body: { ids: string[] } | { all: true }) => api.post('/api/admin/notifications/read', body),
    onMutate: async body => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<NotificationFeed>(key)
      if (prev) {
        const now = new Date().toISOString()
        const items = prev.items.map(n => ('all' in body || body.ids.includes(n.id) ? { ...n, readAt: n.readAt ?? now } : n))
        qc.setQueryData<NotificationFeed>(key, { ...prev, items, unread: 'all' in body ? 0 : Math.max(0, prev.unread - items.filter((n, i) => !prev.items[i].readAt && n.readAt).length) })
      }
      return { prev }
    },
    onError: (e, _b, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); if (!(e instanceof ApiError && e.status === 404)) toast.error(e) },
  })
  const feed = q.data
  const unread = feed?.unread ?? 0
  return (
    <P.Root open={open} onOpenChange={setOpen}>
      <P.Trigger asChild>
        <button type="button" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
          className="relative grid size-8 place-items-center rounded-control text-text-muted transition-colors hover:bg-chrome-hover hover:text-text focus-visible:outline-2 focus-visible:outline-focus data-[state=open]:bg-chrome-hover data-[state=open]:text-text">
          <Bell className="size-[18px]" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-none font-semibold text-white ring-2 ring-chrome tabular">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content align="end" sideOffset={6} collisionPadding={8}
          className="z-[60] flex max-h-[min(560px,calc(100dvh-80px))] w-[380px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-card border border-border bg-surface-raised shadow-popover data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out">
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h2 className="text-base font-semibold">Notifications</h2>
            {unread > 0 && <Button size="sm" variant="ghost" icon={<CheckCheck />} onClick={() => markRead.mutate({ all: true })}>Mark all read</Button>}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {q.isLoading && <div className="flex flex-col gap-3 p-4">{[0, 1, 2].map(i => <div key={i} className="flex gap-3"><span className="flex h-5 shrink-0 items-center"><Skeleton className="size-2" rounded="full" /></span><div className="flex flex-1 flex-col gap-2"><Skeleton className="h-3 w-3/4" /><Skeleton className="h-3 w-1/2" /></div></div>)}</div>}
            {q.isError && <p className="p-6 text-center text-base text-text-muted">{errorMessage(q.error)} <button className="font-medium text-primary-strong hover:underline" onClick={() => q.refetch()}>Retry</button></p>}
            {feed && feed.items.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                <span className="grid size-10 place-items-center rounded-full bg-surface-sunken text-text-subtle">{feed.unavailable ? <BellOff className="size-5" /> : <Bell className="size-5" />}</span>
                <p className="text-base font-medium text-text">{feed.unavailable ? 'Notifications are on their way' : 'You’re all caught up'}</p>
                <p className="text-sm text-text-muted">New orders, payment issues and reviews will show up here.</p>
              </div>
            )}
            {feed && feed.items.length > 0 && (
              <ul className="flex flex-col py-1">
                {feed.items.slice(0, 50).map(n => (
                  <li key={n.id}>
                    <button type="button"
                      onClick={() => { if (!n.readAt) markRead.mutate({ ids: [n.id] }); if (n.link) { setOpen(false); void go(n.link) } }}
                      className="flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none">
                      <span className="flex h-5 shrink-0 items-center"><span className={cn('size-2 rounded-full', n.readAt ? 'bg-transparent' : 'bg-primary')} aria-label={n.readAt ? undefined : 'Unread'} /></span>
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className={cn('text-base', n.readAt ? 'text-text-muted' : 'font-medium text-text')}>{n.title}</span>
                        {n.body && <span className="line-clamp-2 text-sm text-text-muted">{n.body}</span>}
                        <span className="text-xs text-text-subtle">{relativeTime(n.createdAt)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </P.Content>
      </P.Portal>
    </P.Root>
  )
}

// ─── User menu ──────────────────────────────────────────────────────────────────────────

export function useLogout() {
  const qc = useQueryClient()
  return async () => {
    try { await api.post('/api/auth/logout', {}) } catch { /* the cookie may already be gone */ }
    qc.clear()
    window.location.assign('/login')
  }
}

export function UserMenu({ onShortcuts, showStores = true }: { onShortcuts: () => void; showStores?: boolean }) {
  const { me } = useSession()
  const theme = useTheme()
  const logout = useLogout()
  const go = useGuardedNavigate()
  const [profile, setProfile] = useState(false)
  if (!me) return null
  const name = me.user.name || me.user.email
  return (
    <>
      <DropdownMenu contentClassName="w-64" trigger={
        <button type="button" aria-label="Account menu" className="grid size-8 place-items-center rounded-full outline-none transition-shadow hover:ring-2 hover:ring-border-strong focus-visible:ring-2 focus-visible:ring-focus data-[state=open]:ring-2 data-[state=open]:ring-border-strong">
          <Avatar name={name} size={28} />
        </button>
      }>
        <div className="flex items-center gap-2 px-2 pt-2 pb-2">
          <Avatar name={name} size={32} />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-base font-medium text-text">{me.user.name || 'Your account'}</span>
            <span className="truncate text-sm text-text-muted">{me.user.email}</span>
          </div>
        </div>
        <MenuSeparator />
        <MenuItem icon={<UserRound />} onSelect={() => setProfile(true)}>Profile</MenuItem>
        {showStores && <MenuItem icon={<LayoutGrid />} onSelect={() => void go('/stores')}>All stores</MenuItem>}
        {me.user.isPlatformAdmin && <MenuItem icon={<Shield />} onSelect={() => void go('/platform')}>Platform admin</MenuItem>}
        <MenuSeparator />
        <MenuLabel>Theme</MenuLabel>
        <MenuRadioGroup<ThemePreference> value={theme.preference} onValueChange={setThemePreference}
          options={[{ value: 'light', label: 'Light', icon: <Sun /> }, { value: 'dark', label: 'Dark', icon: <Moon /> }, { value: 'system', label: 'System', icon: <Monitor /> }]} />
        <MenuSeparator />
        <MenuItem icon={<Keyboard />} shortcut="?" onSelect={onShortcuts}>Keyboard shortcuts</MenuItem>
        <MenuItem icon={<LogOut />} onSelect={() => void logout()}>Log out</MenuItem>
      </DropdownMenu>
      <ProfileDialog open={profile} onOpenChange={setProfile} />
    </>
  )
}

function ProfileDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { me, refresh } = useSession()
  const [name, setName] = useState(me?.user.name ?? '')
  const [pw, setPw] = useState({ current: '', next: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<'name' | 'password' | null>(null)
  useEffect(() => { if (open) { setName(me?.user.name ?? ''); setPw({ current: '', next: '' }); setErrors({}) } }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const saveName = async () => {
    if (!name.trim()) { setErrors({ name: 'Enter your name' }); return }
    setBusy('name'); setErrors({})
    try { await api.patch('/api/auth/me', { name: name.trim() }); refresh(); toast.success('Profile updated') }
    catch (e) { if (e instanceof ApiError && e.fields) setErrors(e.fields); else toast.error(e) }
    finally { setBusy(null) }
  }
  const savePassword = async () => {
    if (pw.next.length < 8) { setErrors({ next: 'Use at least 8 characters' }); return }
    setBusy('password'); setErrors({})
    try { await withReauth(() => api.post('/api/auth/me/password', pw)); setPw({ current: '', next: '' }); toast.success('Password changed — other sessions were signed out') }
    catch (e) { if (e instanceof ApiError && e.fields) setErrors(e.fields); else if (!(e instanceof ApiError && e.code === 'reauth_cancelled')) toast.error(e) }
    finally { setBusy(null) }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Profile" description={me?.user.email} size="sm">
      <div className="flex flex-col gap-6">
        <form className="flex flex-col gap-3" onSubmit={e => { e.preventDefault(); void saveName() }}>
          <Field label="Name" error={errors.name}><Input value={name} onChange={e => setName(e.target.value)} autoComplete="name" maxLength={80} /></Field>
          <div className="flex justify-end"><Button type="submit" loading={busy === 'name'} disabled={name.trim() === (me?.user.name ?? '')}>Save name</Button></div>
        </form>
        <form className="flex flex-col gap-3 border-t border-border pt-5" onSubmit={e => { e.preventDefault(); void savePassword() }}>
          <h3 className="text-base font-semibold">Change password</h3>
          <Field label="Current password" error={errors.current}><Input type="password" autoComplete="current-password" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} /></Field>
          <Field label="New password" help="At least 8 characters." error={errors.next}><Input type="password" autoComplete="new-password" value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} /></Field>
          <div className="flex justify-end"><Button type="submit" variant="primary" loading={busy === 'password'} disabled={!pw.current || !pw.next}>Change password</Button></div>
        </form>
      </div>
    </Dialog>
  )
}

// ─── Help ───────────────────────────────────────────────────────────────────────────────

export function HelpMenu({ onShortcuts, onPalette }: { onShortcuts: () => void; onPalette: () => void }) {
  return (
    <DropdownMenu trigger={<IconButton icon={<CircleHelp className="size-[18px]!" />} label="Help" className="text-text-muted hover:bg-chrome-hover" />}>
      <MenuItem icon={<Keyboard />} shortcut="?" onSelect={onShortcuts}>Keyboard shortcuts</MenuItem>
      <MenuItem icon={<CommandIcon />} shortcut="mod+k" onSelect={onPalette}>Command menu</MenuItem>
    </DropdownMenu>
  )
}

